import { useEffect, useState, useCallback } from 'react';
import { User } from '@propelauth/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Text,
  Group,
  Stack,
  Button,
  Paper,
  Table,
  Badge,
  Tooltip,
  LoadingOverlay,
  Alert,
  ActionIcon,
  Tabs,
  Pagination,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { env } from '../../utils/env';
import { formatLocalDate, formatDisplayDate, parseLocalDateString } from '../../utils/dateUtils';
import { BudgetPlanResponse, SimpleCategoryHeader, SimpleCategory, convertToSimpleHeader } from '../../types/budget';
import { Transaction, TransactionFormData, TransactionType } from '../../types/transaction';
import PlusIcon from '../../assets/icons/plus.svg';
import RefreshIcon from '../../assets/icons/refresh.svg';
import CalendarIcon from '../../assets/icons/calendar.svg';
import EditIcon from '../../assets/icons/edit.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import RulesIcon from '../../assets/icons/rules.svg';
import pauseIcon from '../../assets/icons/pause.svg';
import InfoIcon from '../../assets/icons/info.svg';
import { TransactionModal } from './TransactionModal';
import { RuleModal } from './RuleModal';
import { Rule, useRules } from '../../hooks/useRules';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';
import { RulesList } from './RulesList';
import { IgnoreTransactionModal } from './IgnoreTransactionModal';
import { useTransactions } from '../../hooks/useTransactions';
import { useCategories } from '../../hooks/useCategories';
import { useAccounts } from '../../hooks/useAccounts';
import { useTransactionRules } from '../../hooks/useTransactionRules';

interface TransactionDisplayProps {
  accessToken: string;
  user: User;
  groupId?: number;
  isVisible?: boolean;
}

export const TransactionDisplay = ({ accessToken, user, groupId, isVisible = true }: TransactionDisplayProps) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [startDate, setStartDate] = useState<Date>(
    new Date(new Date().setDate(1))
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DatesRangeValue | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [showUncategorized, setShowUncategorized] = useState(false);
  const [showIncomes, setShowIncomes] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Modal state
  const [modalOpened, setModalOpened] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<TransactionFormData | null>(null);
  const [rulesModalOpened, setRulesModalOpened] = useState(false);
  const [ruleTransaction, setRuleTransaction] = useState<Transaction | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('transactions');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [ignoreModalOpened, setIgnoreModalOpened] = useState(false);
  const [ignoreTransaction, setIgnoreTransaction] = useState<Transaction | null>(null);
  const [rulesListRefreshCounter, setRulesListRefreshCounter] = useState<number>(0);

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
    totalPages: fetchedTotalPages,
  } = useTransactions(accessToken, {
    startDate: startDate,
    endDate: endDate,
    search: searchQuery,
    categoryIds: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
    showUncategorized: showUncategorized,
    showIncomes: showIncomes,
    page: currentPage,
    pageSize: pageSize,
    isVisible: isVisible,
  });

  const transactions = transactionsData?.items || [];
  const totalPages = fetchedTotalPages || 0;

  const {
    categories,
    isLoading: categoriesLoading
  } = useCategories(accessToken, { isVisible });

  const {
    accounts: financialAccounts,
    isLoading: accountsLoading,
  } = useAccounts(accessToken, { isVisible });

  const {
    loading: rulesLoading,
    applyRuleOnce,
  } = useRules(accessToken, 'transaction', undefined, { isVisible });

  const isLoading = transactionsLoading || categoriesLoading || rulesLoading || accountsLoading;

  const handleTransactionUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    refetchTransactions();
  }, [queryClient, refetchTransactions]);

  const handleCategoryChange = (value: string | null) => {
    setCategoryFilter(value);
    if (value === null) {
      setSelectedCategoryIds([]);
    } else {
      const categoryObj = categories?.find((cat: SimpleCategoryHeader) => cat.id.toString() === value);
      if (categoryObj) {
        setSelectedCategoryIds([categoryObj.id.toString()]);
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleApplyRule = async (transactionId: string, ruleId: string) => {
    if (!accessToken) return;
    try {
      console.warn("applyRuleToTransaction functionality needs to be connected to useRules or a similar hook.");
      notifications.show({ title: 'Rule Application', message: `Rule ${ruleId} application to transaction ${transactionId} needs to be implemented.`, color: 'yellow' });
      handleTransactionUpdated();
    } catch (err) {
      console.error('Error applying rule:', err);
    }
  };

  const handleClearFilters = () => {
    setDateRange(null);
    setSearchQuery('');
    setCategoryFilter(null);
    setSelectedCategoryIds([]);
    setShowUncategorized(false);
    setShowIncomes(false);
  };

  const renderTransactionSummary = () => {
    const totalIncome = transactions
      .filter((t: Transaction) => t.transaction_type === TransactionType.INCOME)
      .reduce((sum: number, t: Transaction) => sum + Math.abs(t.amount), 0);
    const totalExpenses = transactions
      .filter((t: Transaction) => t.transaction_type === TransactionType.EXPENSE)
      .reduce((sum: number, t: Transaction) => sum + Math.abs(t.amount), 0);
    const netTotal = totalIncome - totalExpenses;

    const currencyCodes = transactions.map((t: Transaction) => t.iso_currency_code || 'USD');
    const defaultCurrency = currencyCodes.length > 0
      ? currencyCodes.reduce((a: string, b: string) =>
        currencyCodes.filter((v: string) => v === a).length >= currencyCodes.filter((v: string) => v === b).length ? a : b
      )
      : 'USD';

    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: defaultCurrency
    });

    return (
      <>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Total Income</Text>
          <Text size="xl" fw={500} c="green">
            +{formatter.format(totalIncome)}
          </Text>
        </Stack>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Total Expenses</Text>
          <Text size="xl" fw={500} c="red">
            -{formatter.format(totalExpenses)}
          </Text>
        </Stack>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Net Total</Text>
          <Text size="xl" fw={500} c={netTotal >= 0 ? 'green' : 'red'}>
            {formatter.format(netTotal)}
          </Text>
        </Stack>
      </>
    );
  };

  const handleCreateHeader = async (name: string): Promise<SimpleCategoryHeader> => {
    const planResponse = await fetch(`${env.API_URL}/api/budget/plan`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
    });

    if (!planResponse.ok) {
      throw new Error('Failed to fetch active budget plan');
    }

    const planData = await planResponse.json() as BudgetPlanResponse;
    const planId = planData.id;

    const response = await fetch(`${env.API_URL}/api/budget/plan/${planId}/headers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create header: ${response.status}`);
    }

    const headerResponse = await response.json();
    const newHeader = convertToSimpleHeader(headerResponse);

    queryClient.invalidateQueries({ queryKey: ['budget', 'headers'] });

    return newHeader;
  };

  const handleCreateCategory = async (name: string, headerId: string): Promise<SimpleCategory> => {
    const response = await fetch(`${env.API_URL}/api/budget/headers/${headerId}/categories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        name,
        allocated: 0,
        frequency_months: 1
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create category: ${response.status}`);
    }

    const category = await response.json();
    const newCategory: SimpleCategory = {
      id: category.id.toString(),
      name: category.name,
      header_id: headerId
    };

    queryClient.invalidateQueries({ queryKey: ['budget', 'headers'] });

    return newCategory;
  };

  const handleSubmitTransaction = async (values: TransactionFormData) => {
    console.log('TransactionDisplay - Submitting Transaction Values:', values);
    console.log('TransactionDisplay - Header ID:', values.header_id);
    console.log('TransactionDisplay - Category ID:', values.category_id);

    try {
      const { location_manual_input, ...restValues } = values;
      const payload = {
        ...restValues,
        location: location_manual_input ? { manual_input: location_manual_input } : null,
        date: formatLocalDate(values.date),
      };

      console.log("Submitting payload:", payload);

      const response = await fetch(
        values.transaction_id
          ? `${env.API_URL}/api/transactions/${values.transaction_id}`
          : `${env.API_URL}/api/transactions`,
        {
          method: values.transaction_id ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      setModalOpened(false);
      setEditingTransaction(null);
    } catch (err) {
      console.error('Error saving transaction:', err);
      notifications.show({
        title: 'Error',
        message: `Failed to ${editingTransaction ? 'update' : 'add'} transaction`,
        color: 'red',
      });
    }
  };

  const handleRowClick = (transaction: Transaction) => {
    const localDate = parseLocalDateString(transaction.date) || new Date();

    const formData: TransactionFormData = {
      transaction_id: transaction.transaction_id,
      date: localDate,
      amount: transaction.amount,
      description: transaction.description,
      account_id: transaction.account_id,
      account_name: transaction.account_name,
      merchant_name: transaction.merchant_name || undefined,
      header_id: transaction.header_id || undefined,
      header_name: transaction.header_name || undefined,
      category_id: transaction.category_id || undefined,
      category_name: transaction.category_name || undefined,
      notes: transaction.notes || undefined,
      recurring: transaction.recurring,
      recurring_frequency: transaction.recurring_frequency,
      payment_channel: transaction.payment_channel,
      location: transaction.location,
      iso_currency_code: transaction.iso_currency_code,
      transaction_type: transaction.transaction_type,
      transfer_account: transaction.transfer_account,
    };
    setEditingTransaction(formData);
    setModalOpened(true);
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      const response = await fetch(`${env.API_URL}/api/transactions/${transactionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete transaction');
      }

      showSuccessNotification('Transaction deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    } catch (err) {
      console.error('Error deleting transaction:', err);
      showErrorNotification(err, 'Failed to delete transaction');
    }
  };

  const getTransactionColor = (transaction: Transaction): string => {
    switch (transaction.transaction_type) {
      case TransactionType.INCOME:
        return 'green';
      case TransactionType.EXPENSE:
        return 'red';
      case TransactionType.TRANSFER:
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatAmount = (transaction: Transaction): string => {
    const amount = Math.abs(transaction.amount);
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: transaction.iso_currency_code || 'USD'
    });
    const formattedAmount = formatter.format(amount);

    switch (transaction.transaction_type) {
      case TransactionType.INCOME:
        return `+${formattedAmount}`;
      case TransactionType.EXPENSE:
        return `-${formattedAmount}`;
      case TransactionType.TRANSFER:
        return `↔ ${formattedAmount}`;
      default:
        return formattedAmount;
    }
  };

  const handleCreateRule = (transaction: Transaction, e: React.MouseEvent) => {
    e.stopPropagation();
    setRuleTransaction(transaction);
    setEditingRule(null);
    setRulesModalOpened(true);
  };

  const handleIgnoreTransaction = (tx: Transaction, e: React.MouseEvent) => {
    e.stopPropagation();
    setIgnoreTransaction(tx);
    setIgnoreModalOpened(true);
  };

  const confirmIgnoreTransaction = async () => {
    if (!ignoreTransaction) return;
    try {
      const resp = await fetch(`${env.API_URL}/api/transactions/${ignoreTransaction.transaction_id}/ignore`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });
      if (!resp.ok) {
        throw new Error(await resp.text());
      }
      showSuccessNotification('Transaction ignored from budget');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    } catch (err) {
      console.error('Error ignoring transaction', err);
      showErrorNotification(err, 'Failed to ignore transaction');
    } finally {
      setIgnoreModalOpened(false);
      setIgnoreTransaction(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  if (isLoading && !transactions.length) {
    return (
      <Paper radius="md" p="xl" style={{ position: 'relative', minHeight: 200 }}>
        <LoadingOverlay visible={true} />
      </Paper>
    );
  }

  return (
    <Stack className="w-full max-w-3xl mx-auto p-4">
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}

      <Paper radius="md" withBorder>
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab
              value="transactions"
              leftSection={<img src={CalendarIcon} alt="Transactions" width={16} height={16} />}
            >
              Transactions
            </Tabs.Tab>
            <Tabs.Tab
              value="rules"
              leftSection={<img src={RulesIcon} alt="Rules" width={16} height={16} />}
            >
              Rules
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="transactions">
            <Group justify="space-between" p="md">
              <Button
                leftSection={<img src={PlusIcon} alt="Add" width={16} height={16} />}
                size="sm"
                variant="light"
                onClick={() => setModalOpened(true)}
              >
                Add Transaction
              </Button>
              <Group>
                <DatePickerInput
                  size="sm"
                  value={startDate}
                  onChange={(date) => date && setStartDate(date)}
                  leftSection={<img src={CalendarIcon} alt="Calendar" width={16} height={16} />}
                  placeholder="Start Date"
                />
                <DatePickerInput
                  size="sm"
                  value={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  leftSection={<img src={CalendarIcon} alt="Calendar" width={16} height={16} />}
                  placeholder="End Date"
                />
                <Button
                  leftSection={<img src={RefreshIcon} alt="Refresh" width={16} height={16} />}
                  size="sm"
                  variant="subtle"
                  onClick={() => refetchTransactions()}
                  loading={isLoading}
                >
                  Refresh
                </Button>
              </Group>
            </Group>

            {transactions.length > 0 && (
              <Paper withBorder radius="md" p="md" mb="md" mx="md">
                <Group justify="space-between">
                  {renderTransactionSummary()}
                </Group>
              </Paper>
            )}

            <Paper radius="md" style={{ position: 'relative' }}>
              <LoadingOverlay visible={isLoading} />

              {!transactions?.length ? (
                <Text ta="center" c="dimmed" p="lg">
                  No transactions found.
                </Text>
              ) : (
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Account</Table.Th>
                      <Table.Th>Amount</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {transactions.map((transaction: Transaction) => {
                      return (
                        <Table.Tr
                          key={transaction.transaction_id}
                          onClick={() => handleRowClick(transaction)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Td>
                            {formatDisplayDate(transaction.date)}
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              <Text size="sm">{transaction.description}</Text>
                              {transaction.merchant_name && transaction.transaction_type !== TransactionType.TRANSFER && (
                                <Text size="xs" c="dimmed">
                                  {transaction.merchant_name}
                                </Text>
                              )}
                              {transaction.transaction_type === TransactionType.TRANSFER && (
                                <Text size="xs" c="dimmed">
                                  {transaction.merchant_name || transaction.description} ↔ {transaction.transfer_account}
                                </Text>
                              )}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            {transaction.transaction_type === TransactionType.TRANSFER ? (
                              <Badge variant="light" color="blue">Transfer</Badge>
                            ) : transaction.transaction_type === TransactionType.INCOME ? (
                              <Badge variant="light" color="green">Income</Badge>
                            ) : (
                              <Badge variant="light">
                                {transaction.header_name ? `${transaction.header_name} • ` : ''}{transaction.category_name}
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{transaction.account_name}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              c={getTransactionColor(transaction)}
                              fw={500}
                            >
                              {formatAmount(transaction)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {transaction.pending ? (
                              <Tooltip label="Transaction is still pending">
                                <Badge color="yellow">Pending</Badge>
                              </Tooltip>
                            ) : (
                              <Badge color="green">Cleared</Badge>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              <ActionIcon
                                variant="subtle"
                                onClick={() => {
                                  handleRowClick(transaction);
                                }}
                              >
                                <img src={EditIcon} alt="Edit" width={16} height={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={(e) => handleCreateRule(transaction, e)}
                              >
                                <img src={RulesIcon} alt="Create Rule" width={16} height={16} />
                              </ActionIcon>
                              {transaction.group_expense_id ? (
                                <Tooltip label="This transaction is linked to a group expense. Delete the expense in the group section.">
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    disabled
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ backgroundColor: 'transparent' }}
                                  >
                                    <img src={InfoIcon} alt="Info" width={16} height={16} />
                                  </ActionIcon>
                                </Tooltip>
                              ) : transaction.plaid_transaction_id ? (
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={(e) => handleIgnoreTransaction(transaction, e)}
                                >
                                  <img src={pauseIcon} alt="Ignore" width={16} height={16} />
                                </ActionIcon>
                              ) : (
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTransaction(transaction.transaction_id);
                                  }}
                                >
                                  <img src={TrashIcon} alt="Delete" width={16} height={16} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
              {totalPages > 0 && transactions.length > 0 && (
                <Group justify="center" pt="md">
                  <Pagination total={totalPages} value={currentPage} onChange={handlePageChange} />
                </Group>
              )}
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="rules">
            <Group justify="space-between" p="md">
              <Button
                leftSection={<img src={RulesIcon} alt="Add Rule" width={16} height={16} />}
                size="sm"
                variant="light"
                onClick={() => {
                  setRuleTransaction(null);
                  setEditingRule(null);
                  setRulesModalOpened(true);
                }}
              >
                Add Rule
              </Button>
            </Group>
            <Paper withBorder p="md">
              <RulesList
                accessToken={accessToken}
                onSelect={(rule) => {
                  setEditingRule(rule);
                  setRuleTransaction(null);
                  setRulesModalOpened(true);
                }}
                refreshCounter={rulesListRefreshCounter}
                accounts={categories || []}
              />
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Paper>

      <TransactionModal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setEditingTransaction(null);
        }}
        onSubmit={handleSubmitTransaction}
        transaction={editingTransaction || undefined}
        headers={categories || []}
        onCreateHeader={handleCreateHeader}
        onCreateCategory={handleCreateCategory}
        accounts={financialAccounts || []}
      />

      <RuleModal
        opened={rulesModalOpened}
        onClose={() => {
          setRulesModalOpened(false);
          setRuleTransaction(null);
          setEditingRule(null);
        }}
        onSuccess={() => {
          setRulesModalOpened(false);
          setRuleTransaction(null);
          setEditingRule(null);
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
          setRulesListRefreshCounter(prev => prev + 1);
        }}
        accessToken={accessToken}
        transaction={ruleTransaction}
        initialRule={editingRule}
        accounts={financialAccounts || []}
      />

      <IgnoreTransactionModal
        opened={ignoreModalOpened}
        onClose={() => {
          setIgnoreModalOpened(false);
          setIgnoreTransaction(null);
        }}
        onConfirm={confirmIgnoreTransaction}
        transaction={ignoreTransaction}
      />
    </Stack>
  );
}; 